import React from 'react';

/**
 * Global ErrorBoundary — catches any uncaught React render error and shows a
 * user-friendly recovery screen instead of the blank/yellow screen users saw
 * on mobile PWAs after N navigations.
 *
 * Also registers window.onerror + unhandledrejection handlers so JS errors that
 * happen outside the React render loop (async callbacks, axios failures) are
 * captured and displayed with a manual reload option.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: '', at: null };
    this._onWinError = (e) => {
      try {
        // Only surface hard errors that would otherwise blank the app.
        this.setState({
          hasError: true,
          errorMsg: (e.error && (e.error.message || String(e.error))) || e.message || 'Erreur inconnue',
          at: new Date().toISOString(),
        });
      } catch (_) { /* ignore */ }
    };
    this._onUnhandled = (e) => {
      try {
        const reason = e.reason && (e.reason.message || String(e.reason)) || 'Promise rejetée';
        // Ignore silent aborted fetches (very common when unmounting a page mid-request).
        if (/AbortError|Request aborted/i.test(reason)) return;
        this.setState({ hasError: true, errorMsg: reason, at: new Date().toISOString() });
      } catch (_) { /* ignore */ }
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMsg: (error && (error.message || String(error))) || 'Erreur inconnue',
      at: new Date().toISOString(),
    };
  }

  componentDidCatch(error, info) {
    // Log to console for diagnostics via mobile remote debug or the fallback pane.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  componentDidMount() {
    window.addEventListener('error', this._onWinError);
    window.addEventListener('unhandledrejection', this._onUnhandled);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this._onWinError);
    window.removeEventListener('unhandledrejection', this._onUnhandled);
  }

  handleReload = () => {
    // Full reload — clears in-memory state which is what "yellow screen" usually needs.
    try { window.location.href = '/'; } catch (_) { window.location.reload(); }
  };

  handleClearCache = async () => {
    try {
      // Kill any stale service-worker cache that might be serving broken JS.
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-screen"
        style={{
          minHeight: '100vh',
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 460,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
            Un problème est survenu
          </h1>
          <p style={{ marginTop: 8, color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
            L&apos;application a rencontré une erreur imprévue. Vos données ne sont pas affectées —
            elles sont sécurisées côté serveur. Rechargez pour continuer.
          </p>
          {this.state.errorMsg && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: 12 }}>Détails techniques</summary>
              <pre
                style={{
                  marginTop: 8,
                  padding: 8,
                  background: '#f1f5f9',
                  borderRadius: 6,
                  fontSize: 11,
                  color: '#334155',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.errorMsg}
                {this.state.at ? `\nà ${this.state.at}` : ''}
              </pre>
            </details>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              style={{
                flex: 1,
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Recharger l&apos;application
            </button>
            <button
              data-testid="error-boundary-clear-cache"
              onClick={this.handleClearCache}
              style={{
                background: '#fff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Vider le cache
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
